import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, MapPin, Clock, Check, ArrowLeft, ArrowRight, Shield, Star, Users, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanyFormation() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEmirate, setSelectedEmirate] = useState(null);
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [formData, setFormData] = useState({
    service_type: 'company_formation',
    title: '',
    description: '',
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
      setFormData(prev => ({
        ...prev,
        contact_name: parsed.name || '',
        contact_email: parsed.email || ''
      }));
    }
  }, []);

  const rakEmirate = {
    id: 'rak',
    name: 'رأس الخيمة',
    nameEn: 'Ras Al Khaimah',
    flag: '⛰️',
    color: 'from-red-500 to-orange-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    priceRange: '5,000 - 20,000 درهم',
    processingTime: '2-5 أيام عمل',
    freeZones: ['RAKEZ', 'RAK Free Trade Zone', 'RAK Maritime City'],
    advantages: ['أقل التكاليف', 'أسرع التأسيس', 'مرونة عالية', 'قطاع صناعي قوي'],
    available: true
  };

  const otherEmirates = [
    {
      id: 'abu_dhabi',
      name: 'أبوظبي',
      nameEn: 'Abu Dhabi',
      flag: '🏛️',
      color: 'from-amber-500 to-yellow-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      priceRange: '15,000 - 50,000 درهم',
      processingTime: '5-10 أيام عمل',
      freeZones: ['ADGM', 'Masdar City', 'Twofour54', 'Kizad'],
      advantages: ['مركز مالي عالمي', 'بيئة أعمال متطورة', 'موقع استراتيجي', 'بنية تحتية حديثة'],
      available: false
    },
    {
      id: 'dubai',
      name: 'دبي',
      nameEn: 'Dubai',
      flag: '🌆',
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      priceRange: '12,000 - 45,000 درهم',
      processingTime: '3-7 أيام عمل',
      freeZones: ['DMCC', 'JAFZA', 'DIFC', 'Dubai Silicon Oasis', 'Dubai Internet City'],
      advantages: ['مركز أعمال عالمي', 'سهولة التأسيس', 'تنوع المناطق الحرة', 'شبكة عالمية'],
      available: false
    },
    {
      id: 'sharjah',
      name: 'الشارقة',
      nameEn: 'Sharjah',
      flag: '📚',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      priceRange: '8,000 - 25,000 درهم',
      processingTime: '5-10 أيام عمل',
      freeZones: ['SAIF Zone', 'Hamriyah Free Zone', 'Sharjah Media City'],
      advantages: ['تكاليف تنافسية', 'موقع مركزي', 'دعم حكومي', 'بيئة ثقافية'],
      available: false
    },
    {
      id: 'ajman',
      name: 'عجمان',
      nameEn: 'Ajman',
      flag: '🏝️',
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      priceRange: '5,500 - 15,000 درهم',
      processingTime: '3-5 أيام عمل',
      freeZones: ['Ajman Free Zone', 'Ajman Media City'],
      advantages: ['أسعار اقتصادية', 'إجراءات سريعة', 'موقع مميز', 'دعم المستثمرين'],
      available: false
    },
    {
      id: 'fujairah',
      name: 'الفجيرة',
      nameEn: 'Fujairah',
      flag: '🌊',
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      priceRange: '6,000 - 18,000 درهم',
      processingTime: '3-7 أيام عمل',
      freeZones: ['Fujairah Free Zone', 'Creative City'],
      advantages: ['ميناء استراتيجي', 'قطاع نفطي', 'تكاليف معقولة', 'إجراءات مبسطة'],
      available: false
    },
    {
      id: 'uaq',
      name: 'أم القيوين',
      nameEn: 'Umm Al Quwain',
      flag: '🏖️',
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
      priceRange: '5,000 - 12,000 درهم',
      processingTime: '3-5 أيام عمل',
      freeZones: ['UAQ Free Trade Zone'],
      advantages: ['أسعار منخفضة', 'هدوء وخصوصية', 'دعم حكومي', 'موقع ساحلي'],
      available: false
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('يجب تسجيل الدخول أولاً');
      navigate('/login');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/services/request`,
        {
          ...formData,
          title: `طلب تأسيس شركة في ${selectedEmirate?.name || 'الإمارات'}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('تم إرسال طلبك بنجاح!');
      setSelectedEmirate(null);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero Section - Compact */}
      <section className={`py-6 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-gradient-to-r from-emerald-500 to-teal-600'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => navigate('/services')}
            className={`flex items-center gap-2 mb-4 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-white/80 hover:text-white'}`}
          >
            {isAr ? <ArrowRight className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
            {isAr ? 'العودة' : 'Back'}
          </button>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#1E9BD9]/20 border border-[#1E9BD9]/30' : 'bg-white/20'}`}>
              <Building className={`h-8 w-8 ${isDarkMode ? 'text-[#1E9BD9]' : 'text-white'}`} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>
                {isAr ? 'تأسيس الشركات' : 'Company Formation'}
              </h1>
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-white/80'}`}>
                {isAr ? 'أسس شركتك في الإمارات' : 'Start your company in UAE'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emirates Grid */}
      <section className={`py-8 ${isDarkMode ? '' : 'bg-[#f8fafc]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* RAK - Featured Section */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {isAr ? 'متاح الآن' : 'Available Now'}
              </h2>
              <span className="bg-green-500 text-white text-sm px-3 py-1 rounded-full animate-pulse">
                ✓ {isAr ? 'جاهز للتأسيس' : 'Ready to Start'}
              </span>
            </div>
            
            <div 
              className={`rounded-2xl border-4 border-green-500 p-8 transition-all duration-300 cursor-pointer group relative overflow-hidden ${isDarkMode ? 'bg-[#141414] hover:bg-[#1a1a1a]' : 'bg-white shadow-lg hover:shadow-xl'}`}
              onClick={() => navigate('/services/company-formation/chat')}
            >
              {/* Featured Badge */}
              <div className="absolute top-4 start-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm px-4 py-2 rounded-full font-bold flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                الأفضل للتأسيس
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 items-center">
                {/* Info */}
                <div className="text-center md:text-start">
                  <span className="text-6xl mb-4 block">{rakEmirate.flag}</span>
                  <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{rakEmirate.name}</h3>
                  <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{rakEmirate.nameEn}</p>
                </div>

                {/* Price & Time */}
                <div className="space-y-4">
                  <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-red-500/10' : rakEmirate.bgColor}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{isAr ? 'التكلفة التقريبية' : 'Estimated Cost'}</span>
                      <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{rakEmirate.priceRange}</span>
                    </div>
                  </div>
                  <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-red-500/10' : rakEmirate.bgColor}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{isAr ? 'مدة التأسيس' : 'Processing Time'}</span>
                      <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{rakEmirate.processingTime}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {rakEmirate.freeZones.map((zone, idx) => (
                      <span key={idx} className={`text-sm px-3 py-1 rounded-full ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-[#F1F5F9] text-[#475569]'}`}>
                        {zone}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="text-center">
                  <div className="mb-4">
                    {rakEmirate.advantages.map((adv, idx) => (
                      <div key={idx} className={`flex items-center gap-2 text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                        <Check className="h-4 w-4 text-green-500" />
                        <span>{adv}</span>
                      </div>
                    ))}
                  </div>
                  <Button 
                    className={`w-full bg-gradient-to-r ${rakEmirate.color} text-white py-6 text-lg group-hover:shadow-lg transition-all`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/services/company-formation/chat');
                    }}
                  >
                    <Sparkles className="h-5 w-5 me-2" />
                    {isAr ? 'ابدأ مع المساعد الذكي' : 'Start with AI Assistant'}
                    {isAr ? <ArrowLeft className="h-5 w-5 ms-2" /> : <ArrowRight className="h-5 w-5 ms-2" />}
                  </Button>
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-[#64748B]'}`}>{isAr ? 'سيساعدك الذكاء الاصطناعي في جمع المعلومات' : 'AI will help gather information'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Other Emirates - Coming Soon */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{isAr ? 'إمارات أخرى' : 'Other Emirates'}</h2>
              <span className="bg-yellow-500 text-white text-sm px-3 py-1 rounded-full">
                {isAr ? 'قريباً' : 'Coming Soon'}
              </span>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherEmirates.map((emirate) => (
                <div 
                  key={emirate.id}
                  className={`rounded-2xl p-6 opacity-60 relative overflow-hidden ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white shadow-sm border-2 border-gray-200'}`}
                >
                  {/* Coming Soon Overlay */}
                  <div className={`absolute inset-0 backdrop-blur-[1px] z-10 flex items-center justify-center ${isDarkMode ? 'bg-black/50' : 'bg-white/50'}`}>
                    <div className="bg-yellow-500 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg">
                      {isAr ? 'قريباً' : 'Coming Soon'}
                    </div>
                  </div>
                  
                  {/* Header */}
                  <div className="text-center mb-4">
                    <span className="text-4xl mb-2 block">{emirate.flag}</span>
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{emirate.name}</h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{emirate.nameEn}</p>
                  </div>

                  {/* Price & Time */}
                  <div className={`rounded-xl p-4 mb-4 ${isDarkMode ? 'bg-white/5' : emirate.bgColor}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{isAr ? 'التكلفة التقريبية' : 'Est. Cost'}</span>
                      <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{emirate.priceRange}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{isAr ? 'مدة التأسيس' : 'Processing'}</span>
                      <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{emirate.processingTime}</span>
                    </div>
                  </div>

                  {/* Free Zones */}
                  <div className="mb-4">
                    <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{isAr ? 'المناطق الحرة:' : 'Free Zones:'}</p>
                    <div className="flex flex-wrap gap-1">
                      {emirate.freeZones.slice(0, 3).map((zone, idx) => (
                        <span key={idx} className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-[#F1F5F9] text-[#475569]'}`}>
                          {zone}
                        </span>
                      ))}
                      {emirate.freeZones.length > 3 && (
                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-[#64748B]'}`}>+{emirate.freeZones.length - 3}</span>
                      )}
                    </div>
                  </div>

                  <Button 
                    disabled
                    className={`w-full cursor-not-allowed ${isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-300 text-gray-500'}`}
                  >
                    {isAr ? 'قريباً' : 'Coming Soon'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Request Form Modal */}
      {selectedEmirate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className={`bg-gradient-to-r ${selectedEmirate.color} p-6 rounded-t-2xl`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-4xl">{selectedEmirate.flag}</span>
                  <div className="text-white">
                    <h3 className="text-2xl font-bold">تأسيس شركة في {selectedEmirate.name}</h3>
                    <p className="text-white/80">{selectedEmirate.nameEn}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedEmirate(null)}
                  className="text-white/80 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Emirate Details */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className={`${selectedEmirate.bgColor} rounded-xl p-4`}>
                  <p className="text-sm text-[#64748B] mb-1">التكلفة التقريبية</p>
                  <p className="text-lg font-bold text-[#0F172A]">{selectedEmirate.priceRange}</p>
                </div>
                <div className={`${selectedEmirate.bgColor} rounded-xl p-4`}>
                  <p className="text-sm text-[#64748B] mb-1">مدة التأسيس</p>
                  <p className="text-lg font-bold text-[#0F172A]">{selectedEmirate.processingTime}</p>
                </div>
              </div>

              {/* Advantages */}
              <div className="mb-6">
                <p className="font-bold text-[#0F172A] mb-3">المميزات الرئيسية:</p>
                <div className="grid grid-cols-2 gap-2">
                  {selectedEmirate.advantages.map((adv, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-[#475569]">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{adv}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Free Zones */}
              <div className="mb-6">
                <p className="font-bold text-[#0F172A] mb-3">المناطق الحرة المتاحة:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedEmirate.freeZones.map((zone, idx) => (
                    <span key={idx} className={`${selectedEmirate.bgColor} text-[#475569] px-3 py-1 rounded-full text-sm`}>
                      {zone}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4 border-t pt-6">
                <h4 className="font-bold text-[#0F172A]">بيانات التواصل</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>الاسم الكامل</Label>
                    <Input
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>رقم الهاتف</Label>
                    <Input
                      type="tel"
                      required
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="mt-1"
                      dir="ltr"
                    />
                  </div>
                </div>
                <div>
                  <Label>البريد الإلكتروني</Label>
                  <Input
                    type="email"
                    required
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>نوع النشاط التجاري</Label>
                  <Textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1"
                    rows={3}
                    placeholder="صف نوع النشاط التجاري الذي ترغب بتأسيسه..."
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className={`w-full bg-gradient-to-r ${selectedEmirate.color} text-white py-6`}
                >
                  {loading ? 'جاري الإرسال...' : 'إرسال طلب التأسيس'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Why UAE Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-12">لماذا تأسس شركتك في الإمارات؟</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-[#10B981]" />
              </div>
              <h3 className="text-lg font-bold text-[#0F172A] mb-2">ملكية 100%</h3>
              <p className="text-[#475569] text-sm">ملكية أجنبية كاملة للشركة</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-[#10B981]" />
              </div>
              <h3 className="text-lg font-bold text-[#0F172A] mb-2">0% ضرائب</h3>
              <p className="text-[#475569] text-sm">إعفاء ضريبي في المناطق الحرة</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-[#10B981]" />
              </div>
              <h3 className="text-lg font-bold text-[#0F172A] mb-2">تأسيس سريع</h3>
              <p className="text-[#475569] text-sm">إجراءات سريعة ومبسطة</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-[#10B981]" />
              </div>
              <h3 className="text-lg font-bold text-[#0F172A] mb-2">تأشيرات إقامة</h3>
              <p className="text-[#475569] text-sm">تأشيرات للمستثمر والموظفين</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
